import { Dispatch, useEffect, useReducer } from 'react';

const useHoverTimer = (
    hoverState: HoverState,
    delayMS: number,
    dispatch: Dispatch<HoverAction>,
    pendingState: HoverState,
    actionName: HoverAction,
) => {
    useEffect(() => {
        let timeout: number | undefined;

        if (hoverState === pendingState) {
            if (delayMS === 0) {
                dispatch(actionName);
            } else {
                timeout = window.setTimeout(() => {
                    dispatch(actionName);
                }, delayMS);
            }
        } else {
            clearTimeout(timeout);
        }

        return () => {
            clearTimeout(timeout);
        };
    }, [dispatch, actionName, delayMS, hoverState, pendingState]);
};

type HoverState = 'inside' | 'outside' | 'pendingOut' | 'pendingIn' | 'entered';
type HoverAction = 'markInside' | 'markOutside' | 'expireOut' | 'expireIn' | 'forceExpire';

function isHovering(state: HoverState) {
    return state === 'inside' || state === 'pendingOut' || state === 'entered';
}
function isOutside(state: HoverState) {
    return state === 'outside' || state === 'pendingIn';
}

const initialState: HoverState = 'outside';

/**
 * Delays marking hover after a certain time, and then keeps hovering until
 * after a certain time.
 *
 * Captures all the states of that interaction, which is surprisingly complex.
 */
function hoverReducer(state: HoverState, action: HoverAction): HoverState {
    switch (action) {
        case 'markInside':
            if (isOutside(state)) {
                return 'pendingIn';
            }
            return 'inside';
        case 'markOutside':
            if (isHovering(state)) {
                return 'pendingOut';
            }
            return 'outside';
        case 'expireIn':
            if (state === 'pendingIn') {
                return 'entered';
            }
            return state;
        case 'expireOut':
            if (state === 'pendingOut') {
                return 'outside';
            }
            return state;
        case 'forceExpire':
            return 'outside';
        default:
            throw new Error(`Invalid action: '${action}' given state '${state}'`);
    }
}

export interface UseHoverDelayReturn {
    /**
     * True iff still hovering after the inDelayMS and before the outDelayMS.
     */
    isHovering: boolean;

    /**
     * Mark that this element is or has started hovering.
     *
     * `isHovering` will be true after inDelayMS, if hovering is still marked.
     *
     * Typical use is within an onMouseOver event handler.
     */
    markIsHovering: () => void;

    /**
     * Mark that the element is not being hovered over.
     *
     * The `isHovering` variable might still be true, if the timer hasn't expired yet.
     *
     * Typical use is within an onMouseOut event handler.
     */
    markIsNotHovering: () => void;

    /**
     * Forces the hovering state to be false, regardless of the timer
     */
    forceNotHovering: () => void;
}

export default function useHoverDelay(inDelayMS = 0, outDelayMS = 0): UseHoverDelayReturn {
    const [hoverState, dispatch] = useReducer(hoverReducer, initialState);

    useHoverTimer(hoverState, inDelayMS, dispatch, 'pendingIn', 'expireIn');
    useHoverTimer(hoverState, outDelayMS, dispatch, 'pendingOut', 'expireOut');

    return {
        isHovering: isHovering(hoverState),
        markIsHovering: () => dispatch('markInside'),
        markIsNotHovering: () => dispatch('markOutside'),
        forceNotHovering: () => dispatch('forceExpire'),
    };
}
